import React from 'react';

const SUB = { weekly: 'Varje vecka', biweekly: 'Varannan vecka', triweekly: 'Var tredje vecka' };
const KEY = {
  home: 'Kunden är hemma och öppnar',
  dropoff: 'Kunden lämnar nyckel på kontoret senast kl. 12 dagen innan',
  already: 'Nycklar redan lämnade'
};
const DAYS = ['söndagar', 'måndagar', 'tisdagar', 'onsdagar', 'torsdagar', 'fredagar', 'lördagar'];

const windowLines = (windows, sides, prefix, karm, stege) => {
  const lines = [];
  Object.entries(windows || {}).forEach(([type, w]) => {
    if ((w.count || 0) > 0) {
      const key = prefix ? `${prefix}_${type}` : type;
      const dbl = (sides?.[key] || 'double') === 'double';
      lines.push(`${w.label}: ${w.count} st — ${dbl ? 'dubbelsidigt' : 'enkelsidigt'}`);
    }
  });
  if (lines.length > 0) {
    if (karm) lines.push('Karmtvätt: ja');
    if (stege) lines.push('Stege: ja, ta med');
  }
  return lines;
};

const Group = ({ title, lines }) => {
  if (!lines || lines.length === 0) return null;
  return (
    <div className="pt-2" style={{ borderTop: '1px solid #e8e4d9' }}>
      <p className="font-bold mb-1" style={{ color: '#1a1a2e' }}>{title}</p>
      <ul className="space-y-1">
        {lines.map((l, i) => (<li key={i} style={{ color: '#555' }}>• {l}</li>))}
      </ul>
    </div>
  );
};

const BookingDetails = ({ details }) => {
  if (!details) {
    return (
      <div className="p-3 rounded-xl text-xs mb-3" style={{ backgroundColor: '#f8f7f4', border: '1px solid #e8e4d9', color: '#888' }}>
        Ingen detaljinfo sparad för den här bokningen (gammal bokning).
      </div>
    );
  }
  const d = details;
  const rows = [];
  const add = (label, value) => { if (value) rows.push([label, value]); };
  const day = d.date ? DAYS[new Date(d.date).getDay()] : '';

  if (d.service === 'package') add('Återkommer', `Varje ${day}`);
  else if (d.service === 'minipaket') add('Återkommer', `Varannan ${day}`);
  else if (d.subscription && d.subscription !== 'once') add('Abonnemang', `${SUB[d.subscription]} (${day})`);
  if (d.manual) add('Inlagd', 'Manuellt av admin');
  if (d.dates && d.dates.length > 0) add(d.dates.length > 1 ? `Städdatum (${d.dates.length} st)` : 'Städdatum', d.dates.join(', '));
  if (d.staff && d.staff.length > 0) add('Utfört av', d.staff.map(s => s.name).join(', '));
  if (d.discountPercent > 0) add('Rabatt', `${d.discountPercent}%${d.discountCode ? ` (${d.discountCode})` : ''}`);
  if (d.windowKr > 0 || d.windowNote) add('Fönsterputs', `${d.windowNote || ''}${d.windowKr ? ` — ${d.windowKr} kr` : ''}`);
  if (d.otherKr > 0 || d.otherNote) add('Övriga tillägg', `${d.otherNote || ''}${d.otherKr ? ` — ${d.otherKr} kr` : ''}`);
  if (d.sqm) add('Bostadsyta', `${d.sqm} kvm`);
  if (d.hours) add('Timmar', `${d.hours} h per person`);
  if (d.service !== 'window' && d.service !== 'help') {
    add('Städredskap', d.needsEquipment ? '⚠️ Saknas hos kunden — ta med dammsugare, mopp och hink' : 'Finns hos kunden');
  }
  if (d.hasPets === true) add('Husdjur', (d.petTypes || []).join(', ') || 'Ja');
  if (d.hasPets === false) add('Husdjur', 'Inga');
  add('Nyckel', KEY[d.keyHandling] || d.keyHandling);
  if (d.ovenCount > 0) add('Ugnsrengöring', `${d.ovenCount} st`);
  if (d.fridgeCount > 0) add('Kylskåpsrengöring', `${d.fridgeCount} st`);

  const windowRows = [];
  if (d.windowCleaning || d.service === 'window') {
    windowRows.push(...windowLines(d.windows, d.windowSides, '', d.karmtvätt, d.stege));
    if (d.balkongType?.balkong) windowRows.push(`Balkong: ${d.balkongType.balkong}`);
  }

  const da = d.packageDeepAddons || {};
  const sa = d.packageStandardAddons || {};
  const deepLines = [];
  const stdLines = [];
  if (d.service === 'package') {
    if (da.ovenCount > 0) deepLines.push(`Ugnsrengöring: ${da.ovenCount} st`);
    if (da.fridgeCount > 0) deepLines.push(`Kylskåpsrengöring: ${da.fridgeCount} st`);
    if (da.windowCleaning) deepLines.push(...windowLines(da.windows, d.windowSides, 'deep', da.karmtvätt, da.stege));
    if (sa.ovenCount > 0) stdLines.push(`Ugnsrengöring: ${sa.ovenCount} st`);
    if (sa.fridgeCount > 0) stdLines.push(`Kylskåpsrengöring: ${sa.fridgeCount} st`);
    if (sa.windowCleaning) stdLines.push(...windowLines(sa.windows, d.windowSides, 'std', sa.karmtvätt, sa.stege));
  }

  return (
    <div className="mb-3 rounded-xl overflow-hidden" style={{ border: '2px solid #C9A84C' }}>
      <div className="px-4 py-2" style={{ backgroundColor: '#1a1a2e' }}>
        <p className="text-sm font-bold" style={{ color: '#C9A84C' }}>📋 Kundens val och önskemål</p>
      </div>
      <div className="p-4 space-y-2 text-sm" style={{ backgroundColor: '#fff' }}>
        {rows.map(([label, value], i) => (
          <div key={i} className="flex justify-between gap-4">
            <span style={{ color: '#888' }}>{label}</span>
            <span className="font-semibold text-right" style={{ color: '#1a1a2e' }}>{value}</span>
          </div>
        ))}
        <Group title="Fönsterputs" lines={windowRows} />
        <Group title="Månadspaket: Djupstädning (vecka 1)" lines={deepLines} />
        <Group title="Månadspaket: Standardstädning (vecka 2–4, varje gång)" lines={stdLines} />
        {d.extraNotes && (
          <div className="pt-2" style={{ borderTop: '1px solid #e8e4d9' }}>
            <p className="font-bold mb-1" style={{ color: '#1a1a2e' }}>Kunden vill ha hjälp med</p>
            <p style={{ color: '#555' }}>"{d.extraNotes}"</p>
          </div>
        )}
        {d.notes && (
          <div className="pt-2" style={{ borderTop: '1px solid #e8e4d9' }}>
            <p className="font-bold mb-1" style={{ color: '#1a1a2e' }}>Meddelande</p>
            <p style={{ color: '#555' }}>"{d.notes}"</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default BookingDetails;